import {
  type DrillProgress,
  type Stage,
  hidesCharacter,
  passesPerStage,
  stageOf,
  stages,
} from "../handwriting/xiezi-stages.js";

/**
 * The line over a drill square saying where the reader is, in two parts: what
 * to do, which the page sets in bold, and how far through they are.
 *
 * **The character is named in the first two stages and described in the
 * last two.** An Exercise moves between characters, and the third and fourth
 * stages show no outline to say which one is wanted, so each line says. From
 * the third stage the reader writes from memory, and the character in the
 * line would be the answer, so the line gives its reading and meaning instead
 * (`qíng (clear)`). See `hidesCharacter` in `src/handwriting/xiezi-stages.ts`.
 *
 * **A finished drill is congratulated.** "Well done, you've completed the 秀
 * exercise." in place of what to do, which the page animates. Hugh asked for
 * something more encouraging than a count of stages on 2026-10-15.
 */

/** The character a drill is on, and how to describe it without writing it. */
export interface NamedCharacter {
  readonly character: string;

  /**
   * Its reading and meaning, `qíng (clear)`, where the page knows them. A
   * line in a stage that hides the character says "the character" without.
   */
  readonly prompt?: string | undefined;
}

/** What each stage asks of the reader, with the character in it. */
const stageNames: Readonly<Record<Stage, (named: string) => string>> = {
  cued: (named) => `Trace ${named}, following the stroke shown`,
  traced: (named) => `Trace ${named}`,
  snapped: (named) => `Write ${named}, with strokes snapping into place`,
  free: (named) => `Write ${named} from memory`,
};

/** What a line calls the character, where the reader is. */
function nameOf(progress: DrillProgress, named: NamedCharacter): string {
  if (!hidesCharacter(progress)) {
    return named.character;
  }

  return named.prompt === undefined || named.prompt === ""
    ? "the character"
    : named.prompt;
}

/** The line over the square, in its two parts. */
export interface StageWords {
  /** What to do, or the congratulation once the drill is done. */
  readonly task: string;

  /** Which stage and pass, or nothing once the drill is done. */
  readonly place: string;
}

/**
 * The line over the square saying where the reader is.
 *
 * @param progress - Where they are.
 * @param named - The character being written, and its description.
 * @returns What to do, and the stage and pass they are on.
 */
export function stageWords(
  progress: DrillProgress,
  named: NamedCharacter,
): StageWords {
  const stage = stageOf(progress);

  if (stage === undefined) {
    return {
      task: `Well done, you've completed the ${named.character} exercise.`,
      place: "",
    };
  }

  return {
    task: `${stageNames[stage](nameOf(progress, named))}.`,
    place: `Stage ${String(progress.stage + 1)} of ${String(stages.length)}, pass ${String(progress.pass + 1)} of ${String(passesPerStage)}.`,
  };
}
